<template>
  <div class="asset-view-wrapper">
    <div class="assets-table-container">
      <div class="assets-grid-card">
        <div class="assets-grid-card__header">
          <div class="assets-grid-title">
            <div class="assets-grid-title__icon">
              <i class="bi bi-diagram-3-fill" />
            </div>
            <div>
              <div class="assets-grid-title__text">
                {{ scheme?.name || $t('components.assetSchemes.editor.title') }}
              </div>
              <div class="text-muted small">
                {{ scheme?.group?.name || '-' }}
              </div>
            </div>
          </div>

          <div class="assets-grid-controls">
            <button
              type="button"
              class="assets-toolbar-btn"
              :title="$t('common.back')"
              @click="$router.push({ name: 'AssetSchemes' })"
            >
              <i class="bi bi-arrow-left" />
              <span>{{ $t('common.back') }}</span>
            </button>
            <button
              type="button"
              class="assets-toolbar-btn"
              :title="$t('components.assetSchemes.actions.edit')"
              @click="$router.push({ name: 'AssetSchemeEdit', params: { id } })"
            >
              <i class="bi bi-pencil" />
              <span>{{ $t('components.assetSchemes.actions.edit') }}</span>
            </button>
          </div>
        </div>

        <div class="assets-grid-card__body">
          <div
            v-if="errorMessage"
            class="alert alert-danger d-flex align-items-center gap-2 mb-3"
          >
            <i class="bi bi-exclamation-triangle-fill" />
            <span>{{ errorMessage }}</span>
          </div>
          <div
            v-if="loading"
            class="wp-loading"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.assetSchemes.loading') }}</span>
          </div>
          <div v-else>
            <SchemeEditor
              :scheme-image="schemeImage"
              :markers="schemeMarkers"
              :assets="assetsForGroup"
              :anchors="anchors"
              :read-only="true"
              :calibration="{ widthM: scheme?.widthM ?? null, heightM: scheme?.heightM ?? null }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import SchemeEditor from '@/components/content/SchemeEditor.vue'
import { getAssetScheme } from '@/crud/asset-scheme'
import { getAssetsWithLatestParams } from '@/crud/asset/getters'
import { getAssetGroup } from '@/crud/asset-group'
import { fetchAnchorsForLocation } from '@/composables/useSchemeAnchors'

export default defineComponent({
  name: 'AssetSchemePreviewView',
  components: { SchemeEditor },
  props: {
    id: { type: [String, Number], required: true }
  },
  data() {
    return {
      scheme: null as any,
      schemeImage: null as string | null,
      schemeMarkers: [] as any[],
      assetsCache: [] as any[],
      groupAssets: [] as any[],
      anchors: [] as any[],
      loading: false,
      errorMessage: null as string | null,
      refreshInterval: null as any
    }
  },
  computed: {
    groupId(): number | null {
      return this.scheme?.group?.id ?? null
    },
    assetsForGroup(): any[] {
      if (!this.groupAssets.length) return []
      const live = this.assetsCache || []
      return this.groupAssets.map((asset: any) => {
        const liveAsset = live.find((la: any) => Number(la.id) === Number(asset.id))
        return liveAsset || asset
      })
    }
  },
  mounted() {
    this.fetchScheme()
    this.fetchAssets()
    const refreshMs = parseInt(process.env.VUE_APP_MAP_INTERVAL_REFRESH || '30000')
    this.refreshInterval = setInterval(() => {
      this.fetchAssets()
      this.fetchAnchors()
    }, refreshMs)
  },
  unmounted() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    convertPathToUrl(filePath: string): string {
      let relativePath = filePath
      if (filePath.includes('/public/var/uploads/')) {
        relativePath = filePath.substring(filePath.indexOf('/var/uploads/'))
      } else if (filePath.includes('var/uploads/')) {
        const index = filePath.indexOf('var/uploads/')
        relativePath = '/' + filePath.substring(index)
      }

      if (!relativePath.startsWith('/')) {
        relativePath = '/' + relativePath
      }

      const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '')
      return base ? `${base}${relativePath}` : relativePath
    },
    async fetchScheme() {
      this.loading = true
      this.errorMessage = null
      try {
        const response = await getAssetScheme(this.id as any)
        const scheme = response.data?.scheme || response.data?.data?.scheme || response.data?.scheme
        this.scheme = scheme
        const path = scheme?.filePath || scheme?.file_path
        this.schemeImage = path ? this.convertPathToUrl(path) : null
        this.schemeMarkers = Array.isArray(scheme?.markersJson) ? scheme.markersJson : []
        await Promise.all([
          scheme?.group?.id ? this.fetchGroupAssets(scheme.group.id) : Promise.resolve(),
          this.fetchAnchors(),
        ])
      } catch (e: any) {
        this.errorMessage = e?.response?.data?.error || e?.message || this.$t('components.assetSchemes.error')
      } finally {
        this.loading = false
      }
    },
    async fetchAssets() {
      try {
        const response = await getAssetsWithLatestParams()
        this.assetsCache = response.data?.data || []
      } catch (e: any) {
        this.assetsCache = []
      }
    },
    async fetchGroupAssets(groupId: number) {
      try {
        const response = await getAssetGroup(groupId)
        this.groupAssets = response.data?.assets || response.data?.data?.assets || []
      } catch (e: any) {
        this.groupAssets = []
      }
    },
    async fetchAnchors() {
      this.anchors = await fetchAnchorsForLocation(this.scheme?.location?.id)
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-scheme-editor.scss';
</style>
<style src="@/assets/scss/asset-view-style.scss" lang="scss"></style>
